import React from 'react';
import Dropzone from 'react-dropzone';
import { observer } from 'mobx-react';

import BEM from '../../../../common/bem';
import NewConversation from '../../../../common/components/NewConversation';
import { attachments, mobxInjectSelect } from '../../../../common/utils';
import DotsIndicator from '../../../../common/components/DotsIndicator';
import type { Entity, Conversation } from '../../../../types';
import { PEBanner } from './PEBanner';
import {
  ConversationHeader,
  CurrentConversation,
  DndBanner,
  DelegationBanner,
  DragModal,
  ExistingConvMessageForm,
  NewConvMessageForm,
  NewMessageHeader,
  NewMessageSearchResult,
  NullDropzone,
} from './';
import { useAppSelector } from 'redux-stores';
import { KEYMAP } from 'common/constants';

const classes = BEM.with('ConversationPane');

type MobxProps = {
  peBanner?: string;
  isPatientBannerSettingEnabled: boolean;
  addAttachments: unknown;
  hasCurrentConversation: boolean;
  isComposeTo: boolean;
  isComposing: boolean;
  isInfoPaneOpen: boolean;
  isProviderNetwork: boolean;
  isRecipientPickerOpen: boolean;
  isSwitchingApp: boolean;
  rejectAttachments: unknown;
  selectedFilter: unknown;
  setFilter: (filter: string) => void;
  selectedRecipients: Entity[];
  hideMessageMultiSelect: () => void;
  isDelegationBannerEnabled: boolean;
};

type ConversationPaneProps = {
  hideHeader: boolean;
  hideFooter: boolean;
  currentConversation: Conversation;
};

function ConversationPane({
  addAttachments,
  currentConversation,
  hasCurrentConversation,
  hideFooter = false,
  hideHeader = false,
  isComposeTo,
  isComposing,
  isInfoPaneOpen,
  isProviderNetwork,
  isRecipientPickerOpen,
  isSwitchingApp,
  rejectAttachments,
  selectedFilter,
  setFilter,
  selectedRecipients,
  hideMessageMultiSelect,
  isDelegationBannerEnabled,
  peBanner,
  isPatientBannerSettingEnabled,
}: ConversationPaneProps & MobxProps) {
  const { accessibilityMode } = useAppSelector(({ ui, orgSettings }) => ({
    accessibilityMode: ui.accessibilityMode,
  }));

  const isGroupConversation =
    currentConversation?.counterParty?.$entityType === 'group' &&
    currentConversation?.counterParty?.groupType !== 'ROLE_P2P' &&
    currentConversation?.counterParty?.members !== undefined;

  if (isSwitchingApp) {
    return (
      <div className={classes()}>
        <div className={classes('loading')}>
          <DotsIndicator />
        </div>
      </div>
    );
  }

  let Header: React.ComponentType;
  let Content: React.ComponentType;
  if (isComposeTo) {
    Header = NewMessageHeader;
    Content = NewConversation;
  } else if (isComposing) {
    Header = NewMessageSearchResult;
    Content = NewConversation;
  } else if (hasCurrentConversation) {
    Header = ConversationHeader;
    Content = CurrentConversation;
  } else if (selectedFilter === 'Flagged') {
    return (
      <NullDropzone
        className={classes()}
        innerClassName={classes('placeholder-container-flagged')}
        disabled={accessibilityMode}
      >
        <p className={classes('placeholder-text-flagged')}>You haven’t flagged any conversations</p>
        <button onClick={() => setFilter('Inbox')}>View all conversations</button>
      </NullDropzone>
    );
  } else {
    return (
      <NullDropzone
        className={classes()}
        innerClassName={classes('placeholder-container')}
        disabled={accessibilityMode}
      >
        {selectedFilter !== 'Teams' && (
          <p className={classes('placeholder-text')}>No conversation selected</p>
        )}
      </NullDropzone>
    );
  }

  function renderFooter() {
    if (hideFooter) return '';
    if (isComposeTo || isComposing) {
      return <NewConvMessageForm selectedRecipients={selectedRecipients} />;
    } else if (hasCurrentConversation) {
      return <ExistingConvMessageForm currentConversation={currentConversation} />;
    }
    return '';
  }

  const showDelegationBanner =
    isProviderNetwork &&
    isDelegationBannerEnabled &&
    isGroupConversation &&
    hasCurrentConversation &&
    !isComposing &&
    !isRecipientPickerOpen;

  const showPEBanner =
    peBanner && peBanner.trim().length > 0 && !isProviderNetwork && isPatientBannerSettingEnabled;

  return (
    <main
      role="main"
      aria-labelledby="conversation-heading"
      className={classes()}
      onKeyDown={(e) => {
        if (accessibilityMode && e.key === KEYMAP.ESCAPE) {
          hideMessageMultiSelect();
        }
      }}
    >
      <h2 id="conversation-heading" className="screen-reader-only">
        Conversation with {currentConversation?.counterParty?.name}
      </h2>
      <Dropzone
        className={classes('contents')}
        accept={(attachments as { allContentTypes: string[] }).allContentTypes.join(',')}
        disableClick
        disablePreview
        multiple={true}
        onDropAccepted={addAttachments}
        onDropRejected={rejectAttachments}
        disabled={accessibilityMode}
      >
        {({ isDragActive = false, isDragReject = false } = {}) => (
          <div className={classes('layout')}>
            <DragModal {...{ isDragActive, isDragReject }} />
            {!hideHeader && (
              <div
                className={classes('header', {
                  isInfoPaneOpen,
                })}
                data-test-id="conversation-header"
              >
                <Header />
                {isProviderNetwork && <DndBanner />}
                {showDelegationBanner && <DelegationBanner />}
                {showPEBanner && <PEBanner />}
              </div>
            )}
            <Content />
            {renderFooter()}
          </div>
        )}
      </Dropzone>
    </main>
  );
}

export default mobxInjectSelect<ConversationPaneProps, MobxProps>({
  composeMessageStore: [
    'addAttachments',
    'isComposing',
    'isComposeTo',
    'isRecipientPickerOpen',
    'rejectAttachments',
    'selectedRecipients',
  ],
  conversationStore: ['hasCurrentConversation'],
  rosterStore: ['setFilter', 'selectedFilter'],
  networkStore: ['isProviderNetwork'],
  messengerStore: [
    'isInfoPaneOpen',
    'isSwitchingApp',
    'hideMessageMultiSelect',
    'isDelegationBannerEnabled',
    'peBanner',
    'isPatientBannerSettingEnabled',
  ],
})(observer(ConversationPane));
